import React, { useEffect, useState } from "react";
import Api from "../../../Api/api";
import { toast } from "react-toastify";
import { BeatLoader } from "react-spinners";
import { AES, enc } from "crypto-js";
import { useStateValue } from "../../../states/StateProvider";
import { RefreshCw, Search, Download, AlertCircle, CheckCircle2, Archive, FileText } from "lucide-react";
import { LazyLog, ScrollFollow } from 'react-lazylog';

// Custom CSS for log level colors (adaptable to themes)
const logStyles = `
  .log-error {
    color: #ff6b6b !important;
    background-color: rgba(255, 107, 107, 0.15) !important;
    font-weight: 600 !important;
  }
  .log-warning {
    color: #feca57 !important;
    background-color: rgba(254, 202, 87, 0.15) !important;
    font-weight: 500 !important;
  }
  .log-info {
    color: #54a0ff !important;
    background-color: rgba(84, 160, 255, 0.15) !important;
  }
  .log-debug {
    color: #a4b0be !important;
    background-color: rgba(164, 176, 190, 0.1) !important;
    opacity: 0.8 !important;
  }
  .log-default {
    /* Let theme handle default colors */
  }

  /* Enhance search highlighting */
  .react-lazylog-searchbar {
    background-color: rgba(255, 255, 255, 0.1) !important;
    border: 1px solid rgba(255, 255, 255, 0.3) !important;
  }

  /* Custom scrollbar for better visibility */
  .react-lazylog::-webkit-scrollbar {
    width: 12px;
  }
  .react-lazylog::-webkit-scrollbar-track {
    background: rgba(0,0,0,0.1);
  }
  .react-lazylog::-webkit-scrollbar-thumb {
    background: rgba(255,255,255,0.3);
    border-radius: 6px;
  }
  .react-lazylog::-webkit-scrollbar-thumb:hover {
    background: rgba(255,255,255,0.5);
  }

  /* Force left alignment for all log content */
  .react-lazylog {
    text-align: left !important;
  }
  .react-lazylog * {
    text-align: left !important;
  }
  .react-lazylog .react-lazylog-line {
    text-align: left !important;
    justify-content: flex-start !important;
  }
`;

const Logger = () => {
    // Tab state
    const [activeTab, setActiveTab] = useState('laravel');

    const [logContent, setLogContent] = useState("");
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [lineCount, setLineCount] = useState(500);
    const [logStats, setLogStats] = useState({ size: 0, lastModified: "" });
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [refreshInterval, setRefreshInterval] = useState(null);
    const [followLogs, setFollowLogs] = useState(true);
    const [selectedTheme, setSelectedTheme] = useState('dark');

    // New state for multi-file support
    const [logFiles, setLogFiles] = useState([]);
    const [selectedFile, setSelectedFile] = useState('laravel.log');
    const [loadingFiles, setLoadingFiles] = useState(false);
    const [showArchives, setShowArchives] = useState(false);

    // Migration state
    const [migrationStatus, setMigrationStatus] = useState(null);
    const [showMigration, setShowMigration] = useState(false);
    const [migrationRunning, setMigrationRunning] = useState(false);
    const [migrationResult, setMigrationResult] = useState(null);
    const [verificationResult, setVerificationResult] = useState(null);
    const [splitRunning, setSplitRunning] = useState(false);

    const [{ secretKey }, dispatch] = useStateValue();
    const decryptedBytes = localStorage.getItem('user') ? AES.decrypt(localStorage.getItem('user'), secretKey) : false;
    const user = JSON.parse(decryptedBytes.toString(enc.Utf8));

    // Check if user has access to logger (specific email only)
    const hasLoggerAccess = user?.email === 'kbit.business@gmx.de';

    // Inject custom CSS styles for log levels
    useEffect(() => {
        const styleElement = document.createElement('style');
        styleElement.textContent = logStyles;
        document.head.appendChild(styleElement);

        return () => {
            document.head.removeChild(styleElement);
        };
    }, []);

    useEffect(() => {
        if (hasLoggerAccess) {
            fetchLogFiles();
            fetchLogs();
        }
        return () => {
            if (refreshInterval) {
                clearInterval(refreshInterval);
            }
        };
    }, [hasLoggerAccess]);

    useEffect(() => {
        if (autoRefresh && hasLoggerAccess) {
            const interval = setInterval(() => {
                fetchLogs();
            }, 10000); // Refresh every 10 seconds
            setRefreshInterval(interval);
        } else if (refreshInterval) {
            clearInterval(refreshInterval);
            setRefreshInterval(null);
        }
        return () => {
            if (refreshInterval) {
                clearInterval(refreshInterval);
            }
        };
    }, [autoRefresh, hasLoggerAccess]);

    const fetchLogFiles = async () => {
        setLoadingFiles(true);
        try {
            const response = await Api().post('/getLogFiles');

            if (response.data.success) {
                setLogFiles(response.data.files);
            } else {
                toast.error('Failed to fetch log files list');
            }
        } catch (error) {
            console.error('Error fetching log files:', error);
            toast.error('Error fetching log files list');
        } finally {
            setLoadingFiles(false);
        }
    };

    const fetchLogs = async () => {
        if (!hasLoggerAccess) {
            toast.error('Access denied. Only authorized users can access logs.');
            return;
        }

        setLoading(true);
        try {
            let response;

            if (activeTab === 'email') {
                // Fetch email logs
                response = await Api().post('/getEmailLog', {
                    lines: lineCount,
                    search: searchTerm,
                    from_end: true
                });
            } else {
                // Fetch Laravel logs
                response = await Api().post('/getLaravelLog', {
                    filename: selectedFile,
                    lines: lineCount,
                    search: searchTerm,
                    from_end: true
                });
            }

            if (response.data.success) {
                setLogContent(response.data.content);
                setLogStats({
                    size: response.data.size,
                    lastModified: response.data.lastModified,
                    filename: response.data.filename || 'email_tracking.log'
                });
            } else {
                toast.error(response.data.message || 'Failed to fetch logs');
                setLogContent('');
            }
        } catch (error) {
            console.error('Error fetching logs:', error);
            toast.error('Error fetching logs: ' + (error.response?.data?.message || error.message));
            setLogContent('');
        } finally {
            setLoading(false);
        }
    };

    const handleFileSelect = (filename) => {
        setSelectedFile(filename);
        // Will trigger fetchLogs when selectedFile changes
    };

    useEffect(() => {
        if (hasLoggerAccess && selectedFile) {
            fetchLogs();
        }
    }, [selectedFile]);

    // Reload logs when switching tabs
    useEffect(() => {
        if (hasLoggerAccess) {
            fetchLogs();
        }
    }, [activeTab]);

    const handleSearch = () => {
        fetchLogs();
    };

    const handleClearSearch = () => {
        setSearchTerm("");
        setTimeout(() => {
            fetchLogs();
        }, 100);
    };

    const downloadLogs = () => {
        if (!logContent) {
            toast.warning('No log content to download');
            return;
        }

        const blob = new Blob([logContent], { type: 'text/plain' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${selectedFile.replace('.log', '')}-${new Date().toISOString().split('T')[0]}.txt`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Log file downloaded successfully');
    };

    const downloadSpecificFile = async (filename) => {
        try {
            const response = await Api().post('/downloadLogFile',
                { filename },
                { responseType: 'blob' }
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            toast.success(`Downloaded ${filename}`);
        } catch (error) {
            console.error('Error downloading file:', error);
            toast.error('Error downloading file');
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Migration functions
    const checkMigrationStatus = async () => {
        try {
            const response = await Api().post('/checkLogMigrationStatus');
            if (response.data.success) {
                setMigrationStatus(response.data);
                if (response.data.needs_migration) {
                    setShowMigration(true);
                }
            }
        } catch (error) {
            console.error('Error checking migration status:', error);
        }
    };

    const runMigration = async () => {
        setMigrationRunning(true);
        setMigrationResult(null);
        try {
            const response = await Api().post('/runLogMigration');
            if (response.data.success) {
                setMigrationResult(response.data);
                toast.success('Migration completed successfully!');
                // Refresh status and file list
                await checkMigrationStatus();
                await fetchLogFiles();
            } else {
                toast.error('Migration failed: ' + response.data.message);
                setMigrationResult(response.data);
            }
        } catch (error) {
            console.error('Error running migration:', error);
            toast.error('Migration failed: ' + (error.response?.data?.message || error.message));
        } finally {
            setMigrationRunning(false);
        }
    };

    const splitLog = async () => {
        if (!window.confirm('This will split the current log file into 50 MB chunks. Continue?')) {
            return;
        }

        setSplitRunning(true);
        try {
            const response = await Api().post('/splitLogFile');
            if (response.data.success) {
                if (response.data.split_performed) {
                    toast.success(`Log file split into ${response.data.chunks_created} chunks`);
                } else {
                    toast.info(response.data.message);
                }
                // Refresh file list and status
                await fetchLogFiles();
                await checkMigrationStatus();
            } else {
                toast.error('Failed to split log file: ' + response.data.message);
            }
        } catch (error) {
            console.error('Error splitting log:', error);
            toast.error('Error splitting log: ' + (error.response?.data?.message || error.message));
        } finally {
            setSplitRunning(false);
        }
    };

    const verifyMigration = async () => {
        try {
            const response = await Api().post('/verifyLogMigration');
            if (response.data.success) {
                setVerificationResult(response.data);
                if (response.data.all_passed) {
                    toast.success('All verification checks passed!');
                } else if (response.data.errors.length > 0) {
                    toast.error(`Verification failed with ${response.data.errors.length} errors`);
                } else {
                    toast.warning(`Verification passed with ${response.data.warnings.length} warnings`);
                }
            }
        } catch (error) {
            console.error('Error verifying migration:', error);
            toast.error('Verification failed: ' + error.message);
        }
    };

    // Check migration status on mount
    useEffect(() => {
        if (hasLoggerAccess) {
            checkMigrationStatus();
        }
    }, [hasLoggerAccess]);

    const currentLogFile = logFiles.find(f => f.is_current);
    const archivedLogFiles = logFiles.filter(f => !f.is_current);

    if (!hasLoggerAccess) {
        return (
            <div className='dashboardContainer'>
                <div className='bg-white rounded-md p-10 text-center'>
                    <AlertCircle className="mx-auto mb-4 text-red-500" size={48} />
                    <h2 className='text-2xl mb-4'>Access Denied</h2>
                    <p className='text-gray-600'>You are not authorized to access the Laravel Log Viewer.</p>
                </div>
            </div>
        );
    }

    return (
        <div className='dashboardContainer'>
            <div className='flex justify-between items-center pb-5'>
                <h2 className='text-2xl lg:text-left'>Log Viewer</h2>
                <div className='flex items-center space-x-2'>
                    <button
                        onClick={fetchLogFiles}
                        disabled={loadingFiles}
                        className='flex items-center space-x-2 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md disabled:opacity-50'
                    >
                        <Archive className={`${loadingFiles ? 'animate-spin' : ''}`} size={16} />
                        <span>Refresh List</span>
                    </button>
                    <button
                        onClick={fetchLogs}
                        disabled={loading}
                        className='flex items-center space-x-2 bg-mainBlue hover:bg-blue-700 text-white px-4 py-2 rounded-md disabled:opacity-50'
                    >
                        <RefreshCw className={`${loading ? 'animate-spin' : ''}`} size={16} />
                        <span>Refresh</span>
                    </button>
                    <button
                        onClick={downloadLogs}
                        disabled={!logContent}
                        className='flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md disabled:opacity-50'
                    >
                        <Download size={16} />
                        <span>Download</span>
                    </button>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className='bg-white rounded-md mb-4'>
                <div className='flex border-b border-gray-200'>
                    <button
                        onClick={() => setActiveTab('laravel')}
                        className={`px-6 py-3 font-medium transition-colors ${
                            activeTab === 'laravel'
                                ? 'border-b-2 border-mainBlue text-mainBlue'
                                : 'text-gray-600 hover:text-mainBlue'
                        }`}
                    >
                        Laravel Logs
                    </button>
                    <button
                        onClick={() => setActiveTab('email')}
                        className={`px-6 py-3 font-medium transition-colors ${
                            activeTab === 'email'
                                ? 'border-b-2 border-mainBlue text-mainBlue'
                                : 'text-gray-600 hover:text-mainBlue'
                        }`}
                    >
                        Email Logs
                    </button>
                </div>
            </div>

            {/* Laravel Logs Tab */}
            {activeTab === 'laravel' && (
                <>
            {/* Migration Panel */}
            {migrationStatus && (showMigration || migrationStatus.needs_migration || migrationStatus.can_split) && (
                <div className='bg-gradient-to-r from-silver to-borderBlue border border-borderBlue rounded-md mb-4 p-4'>
                    <div className='flex justify-between items-start mb-3'>
                        <div>
                            <h3 className='text-lg font-semibold text-mainBlue flex items-center'>
                                <AlertCircle size={20} className="mr-2" />
                                Log Management System Setup
                            </h3>
                            <p className='text-sm text-lightBlue mt-1'>
                                {migrationStatus.needs_migration
                                    ? 'Automatic log rotation is not configured. Run migration to enable it.'
                                    : migrationStatus.can_split
                                    ? `Current log file is ${migrationStatus.log_size_mb} MB. Consider splitting it.`
                                    : 'Log management system is ready.'}
                            </p>
                        </div>
                        <button
                            onClick={() => setShowMigration(!showMigration)}
                            className='text-mainBlue hover:text-lightBlueDark text-sm font-medium'
                        >
                            {showMigration ? 'Hide' : 'Show'} Details
                        </button>
                    </div>

                    {showMigration && (
                        <div className='space-y-4'>
                            {/* Status Checks */}
                            <div className='bg-white rounded-md p-4'>
                                <h4 className='font-semibold mb-3 text-text'>Current Status</h4>
                                <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                                    <div className='flex items-center space-x-2'>
                                        {migrationStatus.checks.SizeRotatingLogger ? (
                                            <CheckCircle2 size={18} className="text-green" />
                                        ) : (
                                            <AlertCircle size={18} className="text-red" />
                                        )}
                                        <span className='text-sm'>SizeRotatingLogger Class</span>
                                    </div>
                                    <div className='flex items-center space-x-2'>
                                        {migrationStatus.checks.SizeRotatingHandler ? (
                                            <CheckCircle2 size={18} className="text-green" />
                                        ) : (
                                            <AlertCircle size={18} className="text-red" />
                                        )}
                                        <span className='text-sm'>SizeRotatingHandler Class</span>
                                    </div>
                                    <div className='flex items-center space-x-2'>
                                        {migrationStatus.checks.logging_config ? (
                                            <CheckCircle2 size={18} className="text-green" />
                                        ) : (
                                            <AlertCircle size={18} className="text-red" />
                                        )}
                                        <span className='text-sm'>Logging Configuration</span>
                                    </div>
                                    <div className='flex items-center space-x-2'>
                                        {migrationStatus.checks.api_routes ? (
                                            <CheckCircle2 size={18} className="text-green" />
                                        ) : (
                                            <AlertCircle size={18} className="text-red" />
                                        )}
                                        <span className='text-sm'>API Routes</span>
                                    </div>
                                </div>
                                <div className='mt-3 pt-3 border-t'>
                                    <div className='flex justify-between items-center text-sm'>
                                        <span className='font-medium'>Current Log Size:</span>
                                        <span className={migrationStatus.log_size_mb > 50 ? 'text-orange font-semibold' : 'text-graph'}>
                                            {migrationStatus.log_size_mb} MB
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className='bg-white rounded-md p-4'>
                                <h4 className='font-semibold mb-3 text-text'>Actions</h4>
                                <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
                                    <button
                                        onClick={runMigration}
                                        disabled={migrationRunning || !migrationStatus.needs_migration}
                                        className='flex items-center justify-center space-x-2 bg-mainBlue hover:bg-lightBlueDark text-white px-4 py-3 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-all'
                                    >
                                        {migrationRunning ? (
                                            <>
                                                <BeatLoader size={8} color="white" />
                                                <span>Running...</span>
                                            </>
                                        ) : (
                                            <>
                                                <RefreshCw size={18} />
                                                <span>Run Migration</span>
                                            </>
                                        )}
                                    </button>

                                    <button
                                        onClick={splitLog}
                                        disabled={splitRunning || !migrationStatus.can_split}
                                        className='flex items-center justify-center space-x-2 bg-orange hover:bg-orangeDark text-white px-4 py-3 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-all'
                                    >
                                        {splitRunning ? (
                                            <>
                                                <BeatLoader size={8} color="white" />
                                                <span>Splitting...</span>
                                            </>
                                        ) : (
                                            <>
                                                <Archive size={18} />
                                                <span>Split Log File</span>
                                            </>
                                        )}
                                    </button>

                                    <button
                                        onClick={verifyMigration}
                                        className='flex items-center justify-center space-x-2 bg-green hover:bg-green text-white px-4 py-3 rounded-md transition-all'
                                    >
                                        <CheckCircle2 size={18} />
                                        <span>Verify Setup</span>
                                    </button>
                                </div>
                            </div>

                            {/* Migration Result */}
                            {migrationResult && (
                                <div className='bg-white rounded-md p-4'>
                                    <h4 className='font-semibold mb-3 text-text'>Migration Result</h4>
                                    {migrationResult.success ? (
                                        <div className='space-y-2'>
                                            <div className='flex items-center space-x-2 text-green font-medium'>
                                                <CheckCircle2 size={18} />
                                                <span>{migrationResult.message}</span>
                                            </div>
                                            {migrationResult.steps && migrationResult.steps.map((step, index) => (
                                                <div key={index} className='flex items-center space-x-2 text-sm pl-6'>
                                                    {step.status === 'success' && <CheckCircle2 size={14} className="text-green" />}
                                                    {step.status === 'skipped' && <Archive size={14} className="text-placeholder" />}
                                                    <span className={step.status === 'success' ? 'text-graph' : 'text-placeholder'}>
                                                        {step.name}
                                                        {step.message && ` - ${step.message}`}
                                                    </span>
                                                </div>
                                            ))}
                                            {migrationResult.needs_split && (
                                                <div className='mt-3 p-3 bg-pending border border-orange rounded-md'>
                                                    <p className='text-sm text-orangeDark'>
                                                        Current log file is {migrationResult.log_size_mb} MB.
                                                        Click "Split Log File" to split it into smaller chunks.
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className='text-red'>
                                            <AlertCircle className="inline mr-2" size={18} />
                                            {migrationResult.message}
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Verification Result */}
                            {verificationResult && (
                                <div className='bg-white rounded-md p-4'>
                                    <h4 className='font-semibold mb-3 text-text'>Verification Result</h4>
                                    <div className='space-y-3'>
                                        <div className='flex items-center justify-between p-3 bg-lightgrey rounded-md'>
                                            <span className='font-medium'>Status:</span>
                                            {verificationResult.all_passed ? (
                                                <span className='flex items-center space-x-2 text-green font-semibold'>
                                                    <CheckCircle2 size={18} />
                                                    <span>All Checks Passed</span>
                                                </span>
                                            ) : (
                                                <span className='flex items-center space-x-2 text-orange font-semibold'>
                                                    <AlertCircle size={18} />
                                                    <span>{verificationResult.errors.length} Errors, {verificationResult.warnings.length} Warnings</span>
                                                </span>
                                            )}
                                        </div>

                                        {Object.entries(verificationResult.checks).map(([key, check]) => (
                                            <div key={key} className='flex items-center justify-between text-sm pl-3'>
                                                <span className='text-graph'>{key}:</span>
                                                <span className='flex items-center space-x-2'>
                                                    {check.status === 'success' && <CheckCircle2 size={14} className="text-green" />}
                                                    {check.status === 'warning' && <AlertCircle size={14} className="text-orange" />}
                                                    {check.status === 'error' && <AlertCircle size={14} className="text-red" />}
                                                    {check.status === 'info' && <AlertCircle size={14} className="text-mainBlue" />}
                                                    <span className={
                                                        check.status === 'success' ? 'text-green' :
                                                        check.status === 'warning' ? 'text-orange' :
                                                        check.status === 'error' ? 'text-red' : 'text-mainBlue'
                                                    }>
                                                        {check.message}
                                                    </span>
                                                </span>
                                            </div>
                                        ))}

                                        {verificationResult.warnings.length > 0 && (
                                            <div className='mt-3 p-3 bg-pending border border-orange rounded-md'>
                                                <p className='text-sm font-medium text-orangeDark mb-1'>Warnings:</p>
                                                <ul className='list-disc list-inside text-sm text-orange'>
                                                    {verificationResult.warnings.map((warning, index) => (
                                                        <li key={index}>{warning}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}

                                        {verificationResult.errors.length > 0 && (
                                            <div className='mt-3 p-3 bg-errorBg border border-borderRed rounded-md'>
                                                <p className='text-sm font-medium text-red mb-1'>Errors:</p>
                                                <ul className='list-disc list-inside text-sm text-red'>
                                                    {verificationResult.errors.map((error, index) => (
                                                        <li key={index}>{error}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Help Text */}
                            <div className='bg-silver border border-borderBlue rounded-md p-3'>
                                <p className='text-sm text-mainBlue'>
                                    <strong>What does this do?</strong> The migration sets up automatic log rotation at 50 MB,
                                    preventing memory errors when viewing large log files. The split function divides the current
                                    large log into smaller chunks for easier viewing.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Log Files List */}
            <div className='bg-white rounded-md mb-4 p-4'>
                <div className='flex justify-between items-center mb-3'>
                    <h3 className='text-lg font-semibold flex items-center'>
                        <FileText size={20} className="mr-2" />
                        Log Files ({logFiles.length})
                    </h3>
                    {archivedLogFiles.length > 0 && (
                        <button
                            onClick={() => setShowArchives(!showArchives)}
                            className='text-sm text-mainBlue hover:underline'
                        >
                            {showArchives ? 'Hide' : 'Show'} Archives ({archivedLogFiles.length})
                        </button>
                    )}
                </div>

                {/* Current Log File */}
                {currentLogFile && (
                    <div className='mb-3'>
                        <p className='text-sm text-gray-500 mb-1 font-semibold'>Current Log</p>
                        <div
                            onClick={() => handleFileSelect(currentLogFile.name)}
                            className={`p-3 border rounded-md cursor-pointer transition-all ${
                                selectedFile === currentLogFile.name
                                    ? 'border-mainBlue bg-blue-50'
                                    : 'border-gray-300 hover:border-mainBlue'
                            }`}
                        >
                            <div className='flex justify-between items-center'>
                                <div className='flex items-center space-x-3'>
                                    <FileText size={18} className={selectedFile === currentLogFile.name ? 'text-mainBlue' : 'text-gray-500'} />
                                    <div>
                                        <p className='font-medium'>{currentLogFile.name}</p>
                                        <p className='text-xs text-gray-500'>
                                            {currentLogFile.size_formatted} · Modified: {currentLogFile.modified}
                                        </p>
                                    </div>
                                </div>
                                <div className='flex items-center space-x-2'>
                                    {selectedFile === currentLogFile.name && (
                                        <CheckCircle2 size={18} className="text-mainBlue" />
                                    )}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            downloadSpecificFile(currentLogFile.name);
                                        }}
                                        className='text-gray-500 hover:text-mainBlue p-1'
                                    >
                                        <Download size={16} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Archived Log Files */}
                {showArchives && archivedLogFiles.length > 0 && (
                    <div>
                        <p className='text-sm text-gray-500 mb-1 font-semibold flex items-center'>
                            <Archive size={14} className="mr-1" />
                            Archived Logs
                        </p>
                        <div className='space-y-2 max-h-60 overflow-y-auto'>
                            {archivedLogFiles.map(file => (
                                <div
                                    key={file.name}
                                    onClick={() => handleFileSelect(file.name)}
                                    className={`p-2 border rounded-md cursor-pointer transition-all ${
                                        selectedFile === file.name
                                            ? 'border-mainBlue bg-blue-50'
                                            : 'border-gray-200 hover:border-gray-400'
                                    }`}
                                >
                                    <div className='flex justify-between items-center'>
                                        <div className='flex items-center space-x-2'>
                                            <Archive size={14} className={selectedFile === file.name ? 'text-mainBlue' : 'text-gray-400'} />
                                            <div>
                                                <p className='text-sm font-medium'>{file.name}</p>
                                                <p className='text-xs text-gray-500'>
                                                    {file.size_formatted} · {file.modified}
                                                </p>
                                            </div>
                                        </div>
                                        <div className='flex items-center space-x-2'>
                                            {selectedFile === file.name && (
                                                <CheckCircle2 size={16} className="text-mainBlue" />
                                            )}
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    downloadSpecificFile(file.name);
                                                }}
                                                className='text-gray-500 hover:text-mainBlue p-1'
                                            >
                                                <Download size={14} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Stats Bar */}
            <div className='bg-white rounded-md mb-4 p-4'>
                <div className='grid grid-cols-1 md:grid-cols-5 gap-4'>
                    <div className='text-center'>
                        <div className='text-sm text-gray-500'>File Size</div>
                        <div className='text-lg font-semibold'>{formatFileSize(logStats.size)}</div>
                    </div>
                    <div className='text-center'>
                        <div className='text-sm text-gray-500'>Last Modified</div>
                        <div className='text-lg font-semibold'>{logStats.lastModified}</div>
                    </div>
                    <div className='text-center'>
                        <div className='text-sm text-gray-500'>Lines Shown</div>
                        <div className='text-lg font-semibold'>{lineCount}</div>
                    </div>
                    <div className='text-center'>
                        <div className='text-sm text-gray-500'>Auto Refresh</div>
                        <div className='flex items-center justify-center space-x-2'>
                            <input
                                type="checkbox"
                                checked={autoRefresh}
                                onChange={(e) => setAutoRefresh(e.target.checked)}
                                className='w-4 h-4'
                            />
                            <span className={autoRefresh ? 'text-green-600' : 'text-gray-600'}>
                                {autoRefresh ? 'On' : 'Off'}
                            </span>
                        </div>
                    </div>
                    <div className='text-center'>
                        <div className='text-sm text-gray-500'>Follow Mode</div>
                        <div className='flex items-center justify-center space-x-2'>
                            <input
                                type="checkbox"
                                checked={followLogs}
                                onChange={(e) => setFollowLogs(e.target.checked)}
                                className='w-4 h-4'
                            />
                            <span className={followLogs ? 'text-green-600' : 'text-gray-600'}>
                                {followLogs ? 'On' : 'Off'}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Controls */}
            <div className='bg-white rounded-md mb-4 p-4'>
                <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
                    <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                            Search in logs
                        </label>
                        <div className='flex space-x-2'>
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                placeholder="Search for error, warning, etc..."
                                className='flex-1 border border-gray-300 rounded-md px-3 py-2'
                            />
                            <button
                                onClick={handleSearch}
                                className='bg-mainBlue hover:bg-blue-700 text-white px-4 py-2 rounded-md'
                            >
                                <Search size={16} />
                            </button>
                            {searchTerm && (
                                <button
                                    onClick={handleClearSearch}
                                    className='bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md'
                                >
                                    Clear
                                </button>
                            )}
                        </div>
                    </div>
                    <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                            Number of lines
                        </label>
                        <select
                            value={lineCount}
                            onChange={(e) => setLineCount(parseInt(e.target.value))}
                            className='w-full border border-gray-300 rounded-md px-3 py-2'
                        >
                            <option value={100}>100 lines</option>
                            <option value={500}>500 lines</option>
                            <option value={1000}>1000 lines</option>
                            <option value={2000}>2000 lines</option>
                            <option value={5000}>5000 lines</option>
                        </select>
                    </div>
                    <div>
                        <label className='block text-sm font-medium text-gray-700 mb-2'>
                            Theme
                        </label>
                        <select
                            value={selectedTheme}
                            onChange={(e) => setSelectedTheme(e.target.value)}
                            className='w-full border border-gray-300 rounded-md px-3 py-2'
                        >
                            <option value="dark">Dark</option>
                            <option value="light">Light</option>
                            <option value="monokai">Monokai</option>
                            <option value="solarized-dark">Solarized Dark</option>
                            <option value="solarized-light">Solarized Light</option>
                            <option value="github">GitHub</option>
                            <option value="vs-dark">VS Code Dark</option>
                        </select>
                    </div>
                    <div className='flex items-end'>
                        <button
                            onClick={fetchLogs}
                            disabled={loading}
                            className='w-full bg-mainBlue hover:bg-blue-700 text-white px-4 py-2 rounded-md disabled:opacity-50'
                        >
                            {loading ? 'Loading...' : 'Apply Filters'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Log Content */}
            <div className='bg-white rounded-md p-4'>
                <div className='mb-2 text-sm text-gray-600'>
                    Viewing: <span className='font-semibold'>{selectedFile}</span>
                </div>
                {loading && (
                    <div className='text-center py-10'>
                        <BeatLoader size='10px' />
                        <p className='mt-2 text-gray-600'>Loading logs...</p>
                    </div>
                )}

                {!loading && logContent && (
                    <div className='rounded-md border border-gray-300' style={{ height: '600px' }}>
                        {followLogs ? (
                            <ScrollFollow
                                startFollowing={true}
                                render={({ follow, onScroll }) => (
                                    <LazyLog
                                        text={logContent}
                                        caseInsensitive={true}
                                        enableSearch={true}
                                        extraLines={1}
                                        scrollToLine={searchTerm ? undefined : 0}
                                        highlight={searchTerm ? [searchTerm] : undefined}
                                        lineClassName={(lineNumber) => {
                                            const line = logContent.split('\n')[lineNumber - 1] || '';
                                            if (line.includes('ERROR:')) return 'log-error';
                                            if (line.includes('WARNING:')) return 'log-warning';
                                            if (line.includes('INFO:')) return 'log-info';
                                            if (line.includes('DEBUG:')) return 'log-debug';
                                            return 'log-default';
                                        }}
                                        style={{
                                            height: '600px',
                                            fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                                            fontSize: '13px',
                                            lineHeight: '1.5',
                                            textAlign: 'left'
                                        }}
                                        theme={selectedTheme}
                                        follow={follow}
                                        onScroll={onScroll}
                                    />
                                )}
                            />
                        ) : (
                            <LazyLog
                                text={logContent}
                                caseInsensitive={true}
                                enableSearch={true}
                                extraLines={1}
                                scrollToLine={searchTerm ? undefined : 0}
                                highlight={searchTerm ? [searchTerm] : undefined}
                                lineClassName={(lineNumber) => {
                                    const line = logContent.split('\n')[lineNumber - 1] || '';
                                    if (line.includes('ERROR:')) return 'log-error';
                                    if (line.includes('WARNING:')) return 'log-warning';
                                    if (line.includes('INFO:')) return 'log-info';
                                    if (line.includes('DEBUG:')) return 'log-debug';
                                    return 'log-default';
                                }}
                                style={{
                                    height: '600px',
                                    fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                                    fontSize: '13px',
                                    lineHeight: '1.5',
                                    textAlign: 'left'
                                }}
                                theme={selectedTheme}
                            />
                        )}
                    </div>
                )}

                {!loading && !logContent && (
                    <div className='text-center py-10'>
                        <AlertCircle className="mx-auto mb-4 text-gray-400" size={48} />
                        <p className='text-gray-600'>No log content found. Try adjusting your search criteria.</p>
                    </div>
                )}
            </div>
                </>
            )}

            {/* Email Logs Tab */}
            {activeTab === 'email' && (
                <>
                    {/* Stats Bar */}
                    <div className='bg-white rounded-md mb-4 p-4'>
                        <div className='grid grid-cols-1 md:grid-cols-5 gap-4'>
                            <div className='text-center'>
                                <div className='text-sm text-gray-500'>File Size</div>
                                <div className='text-lg font-semibold'>{formatFileSize(logStats.size)}</div>
                            </div>
                            <div className='text-center'>
                                <div className='text-sm text-gray-500'>Last Modified</div>
                                <div className='text-lg font-semibold'>{logStats.lastModified}</div>
                            </div>
                            <div className='text-center'>
                                <div className='text-sm text-gray-500'>Lines Shown</div>
                                <div className='text-lg font-semibold'>{lineCount}</div>
                            </div>
                            <div className='text-center'>
                                <div className='text-sm text-gray-500'>Auto Refresh</div>
                                <div className='flex items-center justify-center space-x-2'>
                                    <input
                                        type="checkbox"
                                        checked={autoRefresh}
                                        onChange={(e) => setAutoRefresh(e.target.checked)}
                                        className='w-4 h-4'
                                    />
                                    <span className={autoRefresh ? 'text-green-600' : 'text-gray-600'}>
                                        {autoRefresh ? 'On' : 'Off'}
                                    </span>
                                </div>
                            </div>
                            <div className='text-center'>
                                <div className='text-sm text-gray-500'>Follow Mode</div>
                                <div className='flex items-center justify-center space-x-2'>
                                    <input
                                        type="checkbox"
                                        checked={followLogs}
                                        onChange={(e) => setFollowLogs(e.target.checked)}
                                        className='w-4 h-4'
                                    />
                                    <span className={followLogs ? 'text-green-600' : 'text-gray-600'}>
                                        {followLogs ? 'On' : 'Off'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Controls */}
                    <div className='bg-white rounded-md mb-4 p-4'>
                        <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
                            <div>
                                <label className='block text-sm font-medium text-gray-700 mb-2'>
                                    Search in logs
                                </label>
                                <div className='flex space-x-2'>
                                    <input
                                        type="text"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                        placeholder="Search for email, error, etc..."
                                        className='flex-1 border border-gray-300 rounded-md px-3 py-2'
                                    />
                                    <button
                                        onClick={handleSearch}
                                        className='bg-mainBlue hover:bg-blue-700 text-white px-4 py-2 rounded-md'
                                    >
                                        <Search size={16} />
                                    </button>
                                    {searchTerm && (
                                        <button
                                            onClick={handleClearSearch}
                                            className='bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md'
                                        >
                                            Clear
                                        </button>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className='block text-sm font-medium text-gray-700 mb-2'>
                                    Number of lines
                                </label>
                                <select
                                    value={lineCount}
                                    onChange={(e) => setLineCount(parseInt(e.target.value))}
                                    className='w-full border border-gray-300 rounded-md px-3 py-2'
                                >
                                    <option value={100}>100 lines</option>
                                    <option value={500}>500 lines</option>
                                    <option value={1000}>1000 lines</option>
                                    <option value={2000}>2000 lines</option>
                                    <option value={5000}>5000 lines</option>
                                </select>
                            </div>
                            <div>
                                <label className='block text-sm font-medium text-gray-700 mb-2'>
                                    Theme
                                </label>
                                <select
                                    value={selectedTheme}
                                    onChange={(e) => setSelectedTheme(e.target.value)}
                                    className='w-full border border-gray-300 rounded-md px-3 py-2'
                                >
                                    <option value="dark">Dark</option>
                                    <option value="light">Light</option>
                                    <option value="monokai">Monokai</option>
                                    <option value="solarized-dark">Solarized Dark</option>
                                    <option value="solarized-light">Solarized Light</option>
                                    <option value="github">GitHub</option>
                                    <option value="vs-dark">VS Code Dark</option>
                                </select>
                            </div>
                            <div className='flex items-end'>
                                <button
                                    onClick={fetchLogs}
                                    disabled={loading}
                                    className='w-full bg-mainBlue hover:bg-blue-700 text-white px-4 py-2 rounded-md disabled:opacity-50'
                                >
                                    {loading ? 'Loading...' : 'Apply Filters'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Log Content */}
                    <div className='bg-white rounded-md p-4'>
                        <div className='mb-2 text-sm text-gray-600'>
                            Viewing: <span className='font-semibold'>email_tracking.log</span>
                        </div>
                        {loading && (
                            <div className='text-center py-10'>
                                <BeatLoader size='10px' />
                                <p className='mt-2 text-gray-600'>Loading email logs...</p>
                            </div>
                        )}

                        {!loading && logContent && (
                            <div className='rounded-md border border-gray-300' style={{ height: '600px' }}>
                                {followLogs ? (
                                    <ScrollFollow
                                        startFollowing={true}
                                        render={({ follow, onScroll }) => (
                                            <LazyLog
                                                text={logContent}
                                                caseInsensitive={true}
                                                enableSearch={true}
                                                extraLines={1}
                                                scrollToLine={searchTerm ? undefined : 0}
                                                highlight={searchTerm ? [searchTerm] : undefined}
                                                lineClassName={(lineNumber) => {
                                                    const line = logContent.split('\n')[lineNumber - 1] || '';
                                                    if (line.includes('ERROR:') || line.includes('[ERROR]')) return 'log-error';
                                                    if (line.includes('WARNING:') || line.includes('[WARNING]')) return 'log-warning';
                                                    if (line.includes('INFO:') || line.includes('[INFO]')) return 'log-info';
                                                    if (line.includes('DEBUG:') || line.includes('[DEBUG]')) return 'log-debug';
                                                    return 'log-default';
                                                }}
                                                style={{
                                                    height: '600px',
                                                    fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                                                    fontSize: '13px',
                                                    lineHeight: '1.5',
                                                    textAlign: 'left'
                                                }}
                                                theme={selectedTheme}
                                                follow={follow}
                                                onScroll={onScroll}
                                            />
                                        )}
                                    />
                                ) : (
                                    <LazyLog
                                        text={logContent}
                                        caseInsensitive={true}
                                        enableSearch={true}
                                        extraLines={1}
                                        scrollToLine={searchTerm ? undefined : 0}
                                        highlight={searchTerm ? [searchTerm] : undefined}
                                        lineClassName={(lineNumber) => {
                                            const line = logContent.split('\n')[lineNumber - 1] || '';
                                            if (line.includes('ERROR:') || line.includes('[ERROR]')) return 'log-error';
                                            if (line.includes('WARNING:') || line.includes('[WARNING]')) return 'log-warning';
                                            if (line.includes('INFO:') || line.includes('[INFO]')) return 'log-info';
                                            if (line.includes('DEBUG:') || line.includes('[DEBUG]')) return 'log-debug';
                                            return 'log-default';
                                        }}
                                        style={{
                                            height: '600px',
                                            fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                                            fontSize: '13px',
                                            lineHeight: '1.5',
                                            textAlign: 'left'
                                        }}
                                        theme={selectedTheme}
                                    />
                                )}
                            </div>
                        )}

                        {!loading && !logContent && (
                            <div className='text-center py-10'>
                                <AlertCircle className="mx-auto mb-4 text-gray-400" size={48} />
                                <p className='text-gray-600'>No email log content found. Try adjusting your search criteria.</p>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default Logger;
